// src/services/api/twilioAPI.js
import { createAuthHeaders } from '../../utils/authUtils';

const FIREBASE_FUNCTIONS_BASE_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL;

/**
 * Client-side API service for Twilio Content API operations
 * All requests are authenticated and sent through the backend
 */
class TwilioAPI {
  /**
   * Create a new Twilio Content template
   * @param {Object} templateData - Template data
   * @returns {Promise<Object>} Created template result
   */
  static async createTemplate(templateData) {
    try {
      console.log('TwilioAPI: Creating template', { displayName: templateData.displayName });

      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/twilioContent`);
      url.searchParams.set('operation', 'createTemplate');

      const headers = await createAuthHeaders();
      headers['Content-Type'] = 'application/json';

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers,
        body: JSON.stringify({ templateData }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Create template request was not successful');
      }

      console.log('TwilioAPI: Template created successfully', result.data);
      return result.data;
    } catch (error) {
      console.error('TwilioAPI Error in createTemplate:', error);
      throw new Error(`Failed to create template: ${error.message}`);
    }
  }

  /**
   * Submit a template for WhatsApp approval
   * @param {string} contentSid - Twilio Content SID
   * @param {string} name - Template name (machine name)
   * @param {string} category - Template category (default: 'MARKETING')
   * @returns {Promise<Object>} Approval request result
   */
  static async submitForWhatsAppApproval(contentSid, name, category = 'MARKETING') {
    try {
      console.log('TwilioAPI: Submitting for WhatsApp approval', { contentSid, name });

      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/twilioContent`);
      url.searchParams.set('operation', 'submitForApproval');

      const headers = await createAuthHeaders();
      headers['Content-Type'] = 'application/json';

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers,
        body: JSON.stringify({ contentSid, name, category }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Submit for approval request was not successful');
      }

      console.log('TwilioAPI: Template submitted successfully', result.data);
      return result.data;
    } catch (error) {
      console.error('TwilioAPI Error in submitForWhatsAppApproval:', error);
      throw new Error(`Failed to submit for approval: ${error.message}`);
    }
  }

  /**
   * Get approval status for a template
   * @param {string} contentSid - Twilio Content SID
   * @returns {Promise<Object|null>} Approval status or null
   */
  static async getApprovalStatus(contentSid) {
    try {
      console.log('TwilioAPI: Getting approval status', { contentSid });

      if (!contentSid) {
        throw new Error('contentSid is required');
      }

      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/twilioContent`);
      url.searchParams.set('operation', 'getApprovalStatus');
      url.searchParams.set('contentSid', contentSid);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: await createAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Get approval status request was not successful');
      }

      return result.data || null;
    } catch (error) {
      console.error('TwilioAPI Error in getApprovalStatus:', error);
      throw new Error(`Failed to get approval status: ${error.message}`);
    }
  }

  /**
   * Get a template by content SID
   * @param {string} contentSid - Twilio Content SID
   * @returns {Promise<Object>} Template data
   */
  static async getTemplate(contentSid) {
    try {
      console.log('TwilioAPI: Getting template', { contentSid });

      if (!contentSid) {
        throw new Error('contentSid is required');
      }

      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/twilioContent`);
      url.searchParams.set('operation', 'getTemplate');
      url.searchParams.set('contentSid', contentSid);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: await createAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Get template request was not successful');
      }

      console.log('TwilioAPI: Template retrieved successfully', result.data);
      return result.data;
    } catch (error) {
      console.error('TwilioAPI Error in getTemplate:', error);
      throw new Error(`Failed to get template: ${error.message}`);
    }
  }
}

export default TwilioAPI;
